import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * Illinois Professional License Renewal Landing Page
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const ILLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 24,
      cycleYears: 2,
      terminology: 'LU',
      highlights: ['24 LU biennial', 'Min 8 LU in Health, Safety & Welfare', '4 LU in Sustainable Design (mandatory)', 'Max 8 LU self-study', 'Min 1 contact hour per LU'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Architecture',
      boardUrl: 'https://idfpr.illinois.gov/profs/arch.html'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '1 PDH Ethics or Professional Practice (mandatory)', 'Technical engineering subjects', 'Min 15 PDH per year', 'Max 15 PDH self-study'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Engineers',
      boardUrl: 'https://idfpr.illinois.gov/profs/pe.html'
    },
    {
      id: 'PLS',
      name: 'Professional Land Surveyor',
      icon: '📐',
      credits: 30,
      cycleYears: 2,
      terminology: 'PDH',
      highlights: ['30 PDH biennial', '1 PDH Ethics or Professional Practice (mandatory)', 'Land surveying-related topics', 'Min 15 PDH per year', 'Professional development activities'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Land Surveyors',
      boardUrl: 'https://idfpr.illinois.gov/profs/pls.html'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 120,
      cycleYears: 3,
      terminology: 'CPE',
      highlights: ['120 CPE hours triennial', 'Min 20 hours annually', '4 hours Ethics (first renewal)', '4 hours Professional Conduct (subsequent)', 'Min 50% technical subjects'],
      stateBoard: 'Illinois Board of Examiners - Public Accountancy',
      boardUrl: 'https://www.ilboe.org/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 20,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['20 contact hours biennial', 'Max 10 hours self-study', 'Must be nursing-related', 'First renewal exempt if licensed <2 years', 'Approved CE providers required'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Nursing',
      boardUrl: 'https://idfpr.illinois.gov/profs/nursing.html'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 20,
      cycleYears: 2,
      terminology: 'contact hours',
      highlights: ['20 contact hours biennial', 'Advanced practice nursing topics', 'Max 10 hours self-study', 'Must maintain national certification', 'Pharmacology strongly recommended'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - APRN',
      boardUrl: 'https://idfpr.illinois.gov/profs/nursing.html'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '6 hours Ethics (mandatory)', 'Clinical social work practice', 'Max 15 hours self-study', 'Approved CE providers'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Social Work',
      boardUrl: 'https://idfpr.illinois.gov/profs/sw.html'
    },
    {
      id: 'LCPC',
      name: 'Licensed Clinical Professional Counselor',
      icon: '💭',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '6 hours Ethics (mandatory)', 'Clinical counseling topics', 'Max 15 hours self-study', 'Must be relevant to practice'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Professional Counselors',
      boardUrl: 'https://idfpr.illinois.gov/profs/procon.html'
    },
    {
      id: 'LMFT',
      name: 'Licensed Marriage & Family Therapist',
      icon: '👨‍👩‍👧‍👦',
      credits: 30,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['30 hours biennial', '6 hours Ethics (mandatory)', 'Marriage and family therapy topics', 'Max 15 hours self-study', 'Systems-based approaches'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - MFT',
      boardUrl: 'https://idfpr.illinois.gov/profs/mft.html'
    },
    {
      id: 'RE',
      name: 'Real Estate Broker',
      icon: '🏠',
      credits: 12,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['12 hours biennial', '3 hours Fair Housing', '3 hours Sexual Harassment Prevention', '3 hours Advertising', '3 hours Risk Management/other topics'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Real Estate',
      boardUrl: 'https://idfpr.illinois.gov/profs/realestate.html'
    },
    {
      id: 'APPR',
      name: 'Certified Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial', '7-hour National USPAP Update (mandatory)', 'Illinois-specific CE requirements', 'AQB-approved education', 'Appraisal practice-related'],
      stateBoard: 'Illinois Department of Financial & Professional Regulation - Appraisers',
      boardUrl: 'https://idfpr.illinois.gov/profs/appraise.html'
    },
    {
      id: 'ATT',
      name: 'Attorney',
      icon: '⚖️',
      credits: 30,
      cycleYears: 2,
      terminology: 'CLE',
      highlights: ['30 CLE hours biennial', '6 hours Professional Responsibility (mandatory)', '6 hours Diversity/Inclusion (mandatory)', 'Min 3 hours per year', 'Max 6 hours self-study annually'],
      stateBoard: 'Illinois Supreme Court - MCLE',
      boardUrl: 'https://www.illinoiscourts.gov/CLE/'
    }
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Illinois Professional License Renewal Requirements",
      "description": "Complete guide to Illinois professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/il-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://idfpr.illinois.gov/",
          "https://www.illinoiscourts.gov/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Illinois Professional Licenses",
        "numberOfItems": 12,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Illinois License Renewal",
            "item": "https://cectrack.com/il-license-renewal"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my Illinois professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in Illinois. Architects and Engineers need 30 hours biennial, CPAs need 120 CPE hours triennial, Nurses need 20 contact hours biennial, Social Workers and Therapists need 30 hours biennial, Real Estate Brokers need 12 hours biennial, and Attorneys need 30 CLE hours biennial."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in Illinois?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The Illinois Department of Financial and Professional Regulation (IDFPR) oversees most professional licenses including Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Real Estate professionals. The Illinois Supreme Court administers attorney licensing through the MCLE Board."
          }
        },
        {
          "@type": "Question",
          "name": "What are the mandatory CE topics for Illinois professionals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Many Illinois professions have mandatory CE topics: Architects need 4 LU in Sustainable Design and 8 LU in HSW; Engineers need 1 PDH in Ethics; CPAs need 4 hours in Ethics/Professional Conduct; Social Workers and Counselors need 6 hours in Ethics; Real Estate Brokers need specific hours in Fair Housing, Sexual Harassment Prevention, and Advertising; Attorneys need 6 hours in Professional Responsibility and 6 hours in Diversity/Inclusion."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my Illinois professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most Illinois professions renew biennially (every 2 years), including Architects, Engineers, Nurses, Social Workers, Counselors, Therapists, Real Estate professionals, Appraisers, and Attorneys. CPAs renew every 3 years (triennially)."
          }
        }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Illinois Department of Financial and Professional Regulation",
      "alternateName": "IDFPR",
      "url": "https://idfpr.illinois.gov/",
      "telephone": "+1-888-473-4858",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "100 West Randolph Street, Suite 9-300",
        "addressLocality": "Chicago",
        "addressRegion": "IL",
        "postalCode": "60601",
        "addressCountry": "US"
      },
      "areaServed": "Illinois",
      "description": "Regulates professional licensing for Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, and Real Estate professionals in Illinois"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "Illinois Supreme Court - MCLE",
      "url": "https://www.illinoiscourts.gov/CLE/",
      "telephone": "+1-217-558-2200",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "200 East Capitol Avenue",
        "addressLocality": "Springfield",
        "addressRegion": "IL",
        "postalCode": "62701",
        "addressCountry": "US"
      },
      "areaServed": "Illinois",
      "description": "Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Illinois"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="Illinois Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to Illinois professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, Attorneys, and more."
        keywords="Illinois license renewal, IL CE requirements, IL continuing education, Illinois professional licenses, IDFPR, Illinois nurses, IL CPAs, IL engineers, IL attorneys"
        canonical="https://cectrack.com/il-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                Illinois Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your Illinois professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="12 professions">12</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="Over 55 categories">55+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>IDFPR Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e40af', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                Illinois CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#dbeafe',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e40af' }}>
                        {prof.credits} {prof.terminology}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 2 ? 'Biennial' : prof.cycleYears === 3 ? 'Triennial' : `Every ${prof.cycleYears} Years`}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your Illinois License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple Illinois professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all IL professions',
                  'Upload certificates and match to mandatory topics',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#3b82f6', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of Illinois professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              Illinois State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for Illinois professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Illinois Department of Financial & Professional Regulation
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates Architects, Engineers, CPAs, Nurses, Social Workers, Counselors, Therapists, Real Estate professionals, and Appraisers in Illinois.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+18884734858" aria-label="Call IDFPR at 888-473-4858">(888) 473-4858</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://idfpr.illinois.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit IDFPR website">
                      idfpr.illinois.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 100 West Randolph Street, Suite 9-300, Chicago, IL 60601
                  </div>
                  <a 
                    href="https://idfpr.illinois.gov/profs.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all IDFPR professions"
                  >
                    View All Professions
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    Illinois Supreme Court - MCLE
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Administers Minimum Continuing Legal Education (MCLE) requirements for attorneys licensed in Illinois.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+12175582200" aria-label="Call MCLE at 217-558-2200">(217) 558-2200</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.illinoiscourts.gov/CLE/" target="_blank" rel="noopener noreferrer" aria-label="Visit MCLE website">
                      illinoiscourts.gov/CLE
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 200 East Capitol Avenue, Springfield, IL 62701
                  </div>
                  <a 
                    href="https://www.illinoiscourts.gov/CLE/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View MCLE requirements"
                  >
                    View MCLE Requirements
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="IL" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your Illinois License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across Illinois who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default ILLicenseRenewal;
